import {
	components,
	MenuListProps,
	MenuProps,
	ControlProps,
	CSSObjectWithLabel,
	MultiValue,
	SingleValue,
	InputActionMeta,
} from 'react-select';
import { FieldError, RefCallBack } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { ISelectOptions } from 'types/utils';
import classes from '../Select/styles.module.scss';
import { debounce } from 'lodash';
import React from 'react';
import { useEffect } from 'react';

type LoadOptionsFn = (inputValue: string) => Promise<ISelectOptions[]>;
type LoadOptionsWithCancel = LoadOptionsFn & { cancel?: () => void };

const customStyles = {
	menuList: (provided: CSSObjectWithLabel) => ({
		...provided,
		paddingBottom: 0,
		paddingTop: 0,
		maxHeight: '200px',
		overflowY: 'auto' as const,
		touchAction: 'auto' as const,
		WebkitOverflowScrolling: 'touch' as const,
	}),
	clearIndicator: () => ({
		marginTop: '4px',
	}),
	control: (provided: CSSObjectWithLabel) => ({
		...provided,
		borderStyle: 'hidden',
		borderBottom: '1.5px solid rgba(97,66,117,.1)',
		borderRadius: 0,
		':hover': {
			borderColor: 'rgba(97,66,117,.1)',
		},
		minHeight: 'auto',
		boxShadow: 'none',
	}),
	option: (provided: CSSObjectWithLabel, { isFocused }: { isFocused: boolean }) => ({
		...provided,
		backgroundColor: isFocused ? 'rgba(0, 0, 0, 0.16)' : 'white',
		color: isFocused ? '#000 !important' : '#6d6e6f',
	}),
	menu: (provided: CSSObjectWithLabel) => ({
		...provided,
		marginTop: 0,
		touchAction: 'auto' as const,
	}),
};

const Menu = (props: MenuProps<ISelectOptions>) => {
	return <components.Menu {...props}>{props.children}</components.Menu>;
};

const MenuList = (props: MenuListProps<ISelectOptions>) => {
	return (
		<components.MenuList {...props} className={classes['select-items']}>
			{props.children}
		</components.MenuList>
	);
};

const LoadingIndicator = () => {
	return <></>;
};

const Control = (props: ControlProps<ISelectOptions>) => {
	const { selectProps } = props;
	return (
		<components.Control
			{...props}
			className={`${classes['select-selected']} ${selectProps.menuIsOpen && classes['select-arrow-active']}`}
		>
			{props.children}
		</components.Control>
	);
};

const DropdownIndicator = () => {
	return null;
};

const IndicatorSeparator = () => {
	return null;
};

// Константные компоненты для сообщений
const NoOptionsMessage = () => <>Не найдено</>;
const LoadingMessage = () => <>Загружается</>;

export function isSingleValue(
	item: MultiValue<ISelectOptions> | SingleValue<ISelectOptions>,
): item is SingleValue<ISelectOptions> {
	if (!item || Array.isArray(item)) {
		return false;
	}
	return true;
}

function filterOption(options: any, defaultValue: any) {
	let defaultOptions = [];
	if (Array.isArray(defaultValue)) {
		defaultOptions = defaultValue
			.map(value => options.find((option: any) => String(option?.value) === String(value)))
			.filter(Boolean);
	} else {
		defaultOptions = options.find((option: ISelectOptions) => String(option.value) === String(defaultValue));
	}
	return defaultOptions;
}

// Вспомогательные функции для упрощения логики
function shouldResetOnBooleanChange(
	prevOptions: ISelectOptions[] | boolean | undefined,
	currentOptions: ISelectOptions[] | boolean | undefined
): boolean {
	const isPrevBoolean = typeof prevOptions === 'boolean';
	const isCurrBoolean = typeof currentOptions === 'boolean';

	if (!isPrevBoolean && !isCurrBoolean) {
		return false;
	}

	const prevBoolean = isPrevBoolean ? (prevOptions as boolean) : null;
	const currBoolean = isCurrBoolean ? (currentOptions as boolean) : null;

	return prevBoolean !== currBoolean;
}

function hasOptionsChanged(
	prevOptions: ISelectOptions[] | undefined,
	currentOptions: ISelectOptions[] | undefined
): boolean {
	const prevLength = prevOptions?.length || 0;
	const currLength = currentOptions?.length || 0;
	const prevFirst = prevOptions?.[0]?.value;
	const currFirst = currentOptions?.[0]?.value;
	
	return prevLength !== currLength || prevFirst !== currFirst;
}

function handleSingleValueUpdate(
	currentValue: ISelectOptions,
	newOptions: ISelectOptions[],
	onChange: (value: any) => void
): ISelectOptions | null {
	const stillExists = newOptions.find(
		(option: ISelectOptions) => String(option.value) === String(currentValue.value)
	);
	
	if (!stillExists) {
		onChange(null);
		return null;
	}
	
	return currentValue;
}

function handleMultiValueUpdate(
	currentValue: ISelectOptions[],
	newOptions: ISelectOptions[],
	onChange: (value: any) => void
): ISelectOptions[] | null {
	const stillExisting = currentValue.filter((selected) =>
		newOptions.some((option: ISelectOptions) => String(option.value) === String(selected.value))
	);
	
	const newValue = stillExisting.length > 0 ? stillExisting : null;
	
	if (stillExisting.length !== currentValue.length) {
		onChange(newValue ? stillExisting.map((i: ISelectOptions) => i.value) : null);
	}
	
	return newValue;
}

export default function CustomAsyncSelect({
	onChange,
	placeholder = '',
	error,
	isClearable = false,
	label,
	defaultValue,
	isMulti = false,
	defaultOptions,
	load,
	innerRef,
	onInputChange,
	inputValue,
	width = 'auto',
	isDisabled = false,
	debounceTimeout = 500,
}: {
	onChange: (option: string | null | number | (string | number)[]) => void;
	placeholder?: string;
	error?: FieldError;
	isDisabled?: boolean;
	isClearable?: boolean;
	label?: React.ReactNode;
	defaultValue?: number | string;
	isMulti?: boolean;
	defaultOptions: ISelectOptions[] | boolean;
	load?: (inputValue: string) => Promise<ISelectOptions[]>;
	innerRef?: RefCallBack;
	onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
	inputValue?: string;
	width?: string;
	debounceTimeout?: number;
}) {
	const adaptedLoad = React.useCallback(
		(inputValue: string) => {
			if (!load) return Promise.resolve([]);
			return load(inputValue);
		},
		[load]
	);

	const debouncedLoadOptions = React.useMemo<LoadOptionsWithCancel>(() => {
		if (!load) {
			const emptyLoader = ((inputValue: string) => Promise.resolve<ISelectOptions[]>([])) as LoadOptionsWithCancel;
			return emptyLoader;
		}

		if (debounceTimeout <= 0) {
			const immediateLoader = adaptedLoad as LoadOptionsWithCancel;
			immediateLoader.cancel = () => undefined;
			return immediateLoader;
		}

		let currentToken: symbol | null = null;
		let isCancelled = false;

		const debounced = debounce(
			async (inputValue: string, resolve: (value: ISelectOptions[]) => void, reject: (reason?: unknown) => void) => {
				const requestToken: symbol = Symbol('async-select-request');
				currentToken = requestToken;
				isCancelled = false;

				try {
					const result = await adaptedLoad(inputValue);
					if (!isCancelled && currentToken === requestToken) {
						resolve(result);
					}
				} catch (error) {
					if (!isCancelled && currentToken === requestToken) {
						reject(error);
					}
				}
			},
			debounceTimeout,
		);

		const loadWithDebounce = ((inputValue: string) => {
			return new Promise<ISelectOptions[]>((resolve, reject) => {
				debounced(inputValue, resolve, reject);
			});
		}) as LoadOptionsWithCancel;

		loadWithDebounce.cancel = () => {
			isCancelled = true;
			currentToken = null;
			debounced.cancel();
		};

		return loadWithDebounce;
	}, [adaptedLoad, debounceTimeout, load]);
	
	const [internalValue, setInternalValue] = React.useState<ISelectOptions | ISelectOptions[] | null>(null);
	const prevDefaultOptionsRef = React.useRef<ISelectOptions[] | boolean | undefined>(defaultOptions);
	const internalValueRef = React.useRef<ISelectOptions | ISelectOptions[] | null>(internalValue);
	const onChangeRef = React.useRef(onChange);
	
	// Синхронизируем refs
	React.useEffect(() => {
		internalValueRef.current = internalValue;
		onChangeRef.current = onChange;
	}, [internalValue, onChange]);

	// Очищаем таймер при размонтировании
	React.useEffect(() => {
		return () => {
			debouncedLoadOptions.cancel?.();
		};
	}, [debouncedLoadOptions]);

	// Сбрасываем или сохраняем значение при изменении defaultOptions
	React.useEffect(() => {
		// Проверяем, нужно ли сбросить значение при изменении boolean типа
		if (shouldResetOnBooleanChange(prevDefaultOptionsRef.current, defaultOptions)) {
			setInternalValue(null);
			prevDefaultOptionsRef.current = defaultOptions;
			return;
		}
		
		// Работаем только с массивами опций
		if (typeof defaultOptions === 'boolean' || typeof prevDefaultOptionsRef.current === 'boolean') {
			prevDefaultOptionsRef.current = defaultOptions;
			return;
		}
		
		// Проверяем, изменился ли список опций
		if (!hasOptionsChanged(prevDefaultOptionsRef.current, defaultOptions)) {
			return;
		}
		
		const currentValue = internalValueRef.current;
		const currLength = defaultOptions?.length || 0;
		
		// Если новый список пустой, а значение было выбрано - не сбрасываем его
		// (список может быть пустым временно во время перезагрузки)
		if (currLength === 0 && currentValue) {
			prevDefaultOptionsRef.current = defaultOptions;
			return;
		}
		
		// Обрабатываем single select
		if (currentValue && !Array.isArray(currentValue)) {
			const updatedValue = handleSingleValueUpdate(currentValue, defaultOptions, onChangeRef.current);
			if (updatedValue === null) {
				setInternalValue(null);
			}
		}
		// Обрабатываем multi select
		else if (currentValue && Array.isArray(currentValue)) {
			const updatedValue = handleMultiValueUpdate(currentValue, defaultOptions, onChangeRef.current);
			setInternalValue(updatedValue);
		}
		// Если значение не было выбрано - оставляем null
		else if (!currentValue && currLength > 0) {
			setInternalValue(null);
		}
		
		prevDefaultOptionsRef.current = defaultOptions;
	}, [defaultOptions]);

	const handleChange = (newValue: MultiValue<ISelectOptions> | SingleValue<ISelectOptions>): void => {
		setInternalValue(newValue as any);
		if (!newValue) {
			onChange(newValue);
		} else if (Array.isArray(newValue)) {
			onChange(newValue.map((i: ISelectOptions) => i.value));
		} else if (isSingleValue(newValue)) {
			onChange(newValue.value);
		}
	};

	return (
		<label className={classes.label} style={{ width: width }}>
			{label && <span className={classes['label__title']}>{label}</span>}
			<AsyncSelect
				styles={customStyles}
				defaultOptions={defaultOptions}
				className={`${classes['styled-select']} ${classes['styled-select--md']}`}
				loadOptions={debouncedLoadOptions}
				isMulti={isMulti}
				ref={innerRef}
				isClearable={isClearable}
				onChange={handleChange}
				onInputChange={onInputChange}
				placeholder={placeholder}
				inputValue={inputValue}
				noOptionsMessage={NoOptionsMessage}
				loadingMessage={LoadingMessage}
				components={{ MenuList, Menu, Control, DropdownIndicator, IndicatorSeparator, LoadingIndicator }}
				value={internalValue}
				isDisabled={isDisabled}
				menuShouldBlockScroll={false}
				menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
				cacheOptions={false}
			/>
			{error && <small className={classes['label__error']}>{error.message}</small>}
		</label>
	);
}
